import { requestJson } from '../common/http';
import type { CurrentUserResponse } from './models';

export const fetchCurrentUser = (): Promise<CurrentUserResponse> => requestJson<CurrentUserResponse>({ method: 'GET', path: '/me' });

const authHeader = (pat: string): string => `Basic ${btoa(':' + pat)}`;

const adoBase = (org: string): string => `https://dev.azure.com/${encodeURIComponent(org)}`;
const vsspsBase = (org: string): string => `https://vssps.dev.azure.com/${encodeURIComponent(org)}`;

export const fetchAvatarObjectUrl = async (org: string, pat: string): Promise<string | null> => {
    try {
        const connResp = await fetch(`${adoBase(org)}/_apis/connectionData?api-version=7.1-preview`, {
            headers: { Authorization: authHeader(pat) },
        });
        if (!connResp.ok) {
            return null;
        }
        const conn = (await connResp.json()) as {
            authenticatedUser?: { subjectDescriptor?: string };
            authorizedUser?: { subjectDescriptor?: string };
        };
        const descriptor = conn.authenticatedUser?.subjectDescriptor ?? conn.authorizedUser?.subjectDescriptor;
        if (!descriptor) {
            return null;
        }
        return fetchAvatarByDescriptor(org, pat, descriptor);
    } catch {
        return null;
    }
};

const fetchAvatarByDescriptor = async (org: string, pat: string, descriptor: string): Promise<string | null> => {
    const resp = await fetch(
        `${vsspsBase(org)}/_apis/graph/Subjects/${encodeURIComponent(descriptor)}/avatars?size=large&api-version=7.1`,
        { headers: { Authorization: authHeader(pat) } },
    );
    if (!resp.ok) {
        return null;
    }
    const body = (await resp.json()) as { value?: string };
    if (!body.value) {
        return null;
    }
    const binary = atob(body.value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return URL.createObjectURL(new Blob([bytes], { type: 'image/png' }));
};

type GraphUser = {
    descriptor: string;
    mailAddress?: string;
    principalName?: string;
};

// Single graph-users fetch per org+pat — cached for the lifetime of the
// tab. The team is tiny so one page is enough; if it ever grows past
// the ADO default page size, add `?continuationToken=` paging.
const graphUsersCache = new Map<string, Promise<GraphUser[]>>();

const fetchGraphUsers = (org: string, pat: string): Promise<GraphUser[]> => {
    const key = `${org}|${pat}`;
    const cached = graphUsersCache.get(key);
    if (cached) {
        return cached;
    }
    const promise = (async () => {
        try {
            const resp = await fetch(`${vsspsBase(org)}/_apis/graph/users?subjectTypes=aad,msa&api-version=7.1-preview.1`, {
                headers: { Authorization: authHeader(pat) },
            });
            if (!resp.ok) {
                return [];
            }
            const body = (await resp.json()) as { value?: GraphUser[] };
            return body.value ?? [];
        } catch {
            return [];
        }
    })();
    graphUsersCache.set(key, promise);
    return promise;
};

const avatarByEmailCache = new Map<string, Promise<string | null>>();

export const fetchAvatarObjectUrlByEmail = (org: string, pat: string, email: string): Promise<string | null> => {
    const key = `${org}|${email.toLowerCase()}`;
    const cached = avatarByEmailCache.get(key);
    if (cached) {
        return cached;
    }
    const promise = (async () => {
        try {
            const users = await fetchGraphUsers(org, pat);
            const target = email.toLowerCase();
            const user = users.find((u) => u.mailAddress?.toLowerCase() === target || u.principalName?.toLowerCase() === target);
            if (!user) {
                return null;
            }
            return fetchAvatarByDescriptor(org, pat, user.descriptor);
        } catch {
            return null;
        }
    })();
    avatarByEmailCache.set(key, promise);
    return promise;
};
